import { useQueryClient } from '@tanstack/react-query'
import { MoreHorizontal } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import { toast } from 'sonner'

import type { ColumnConfig } from '@/components/tables/enhanced-data-table'
import type { TimelineEvent } from '@/components/ui/vertical-event-timeline'
import type { LeaveRequestFormContext, LeaveRequestFormState } from '@/features/leave/components/LeaveRequestForm'
import type {
  LeaveRequest,
  LeaveRequestCreateDto,
  LeaveRequestUpdateDto,
  MyEligibleLeaveTypesParams,
  MyRequestsParams,
} from '@/utils/api/leave-service/models'

import { EnhancedDataTable } from '@/components/tables/enhanced-data-table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Progress } from '@/components/ui/progress'
import { VerticalEventTimeline } from '@/components/ui/vertical-event-timeline'
import { LeaveRequestForm } from '@/features/leave/components/LeaveRequestForm'
import { handleError } from '@/lib/utils'
import { useMyEligibleLeaveTypes, useMyRequests, useRevertLeave, useTrackLeaveApplication, useUpdateLeave } from '@/utils/api/leave-service'
import { LeaveRequestStatus } from '@/utils/api/leave-service/models'
import { formatDate, formatDateTime } from '@/utils/DateHelpers'

export function LeaveApplicationsTab() {
  const [requestDialogOpen, setRequestDialogOpen] = useState(false)
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false)
  const [trackingDialogOpen, setTrackingDialogOpen] = useState(false)
  const [editDialogOpen, setEditDialogOpen] = useState(false)
  const [revertDialogOpen, setRevertDialogOpen] = useState(false)
  const [activeRequest, setActiveRequest] = useState<LeaveRequest | null>(null)
  const [requestFormKey, setRequestFormKey] = useState(0)
  const queryClient = useQueryClient()

  const requestsParams = useMemo<MyRequestsParams>(() => ({
    pageable: { page: 0, size: 200 },
  }), [])
  const leaveTypesParams = useMemo<MyEligibleLeaveTypesParams>(() => ({
    pageable: { page: 0, size: 200 },
  }), [])

  const myRequestsQuery = useMyRequests(requestsParams)
  const myEligibleLeaveTypesQuery = useMyEligibleLeaveTypes(leaveTypesParams)
  const updateLeaveMutation = useUpdateLeave()
  const revertLeaveMutation = useRevertLeave({
    mutation: {
      onError: (e: import('axios').AxiosError) => handleError(e, 'Failed to cancel leave request. Please try again later.'),
    },
  })
  const leaveRequests = useMemo<LeaveRequest[]>(() => {
    const content = myRequestsQuery.data?.data?.content ?? []
    return content.map((request) => {
      const hasDepartment = request.departmentId !== undefined
        || request.departmentName
        || request.departmentCode
      const hasLeaveType = request.leaveTypeId !== undefined
        || request.leaveTypeName
        || request.leaveTypeCode
      return {
        id: request.id,
        userId: request.userId,
        department: hasDepartment
          ? {
            id: request.departmentId,
            name: request.departmentName,
            code: request.departmentCode,
          }
          : undefined,
        leaveType: hasLeaveType
          ? {
            id: request.leaveTypeId,
            name: request.leaveTypeName,
            code: request.leaveTypeCode,
          }
          : undefined,
        startDate: request.startDate,
        endDate: request.endDate,
        workingDays: request.workingDays,
        daysTaken: request.daysTaken,
        daysRefunded: request.daysRefunded,
        status: request.status as LeaveRequestStatus | undefined,
        reason: request.reason,
        contactAddress: request.contactAddress,
        contactPhone: request.contactPhone,
        attachmentIds: request.attachmentIds,
        currentApprovalLevel: request.currentApprovalLevel,
        requiresCeoFinalApproval: request.requiresCeoFinalApproval,
        coveringUserId: request.coveringUserId,
        createdAt: request.createdAt,
        updatedAt: request.updatedAt,
      }
    })
  }, [myRequestsQuery.data?.data?.content])

  const sortedRequests = useMemo(() => {
    return [...leaveRequests].sort((first, second) => {
      const firstDate = first.createdAt ? new Date(first.createdAt).getTime() : 0
      const secondDate = second.createdAt ? new Date(second.createdAt).getTime() : 0
      return secondDate - firstDate
    })
  }, [leaveRequests])

  const handleRequestDialogChange = useCallback((open: boolean) => {
    setRequestDialogOpen(open)
    if (!open) {
      setRequestFormKey(prev => prev + 1)
    }
  }, [])

  const { refetch: refetchMyRequests } = myRequestsQuery
  const handleRequestSubmitted = useCallback(() => {
    setRequestDialogOpen(false)
    setRequestFormKey(prev => prev + 1)
    void refetchMyRequests()
  }, [refetchMyRequests])

  const handleOpenDetails = useCallback((request: LeaveRequest) => {
    setActiveRequest(request)
    setDetailsDialogOpen(true)
  }, [])

  const handleOpenTracking = useCallback((request: LeaveRequest) => {
    setActiveRequest(request)
    setTrackingDialogOpen(true)
  }, [])

  const handleOpenEdit = useCallback((request: LeaveRequest) => {
    setActiveRequest(request)
    setEditDialogOpen(true)
  }, [])

  const handleOpenRevert = useCallback((request: LeaveRequest) => {
    setActiveRequest(request)
    setRevertDialogOpen(true)
  }, [])

  const handleEditSubmit = useCallback(async (
    payload: LeaveRequestCreateDto,
    context: LeaveRequestFormContext,
  ) => {
    if (!activeRequest?.id) {
      return
    }
    const updatePayload: LeaveRequestUpdateDto = {
      leaveTypeId: payload.leaveTypeId,
      startDate: payload.startDate,
      endDate: payload.endDate,
      reason: payload.reason,
      requestedDays: payload.requestedDays,
      attachmentIds: payload.attachmentIds,
      coveringUserId: context.coveringUserId,
    }
    const response = await updateLeaveMutation.mutateAsync({
      id: activeRequest.id,
      data: updatePayload,
    })
    if (!response?.data?.success) {
      throw new Error(response?.data?.message || 'Unable to update leave request.')
    }
    queryClient.setQueryData(myRequestsQuery.queryKey, (previous) => {
      if (!previous) {
        return previous
      }
      const previousData = previous.data
      const content = previousData?.content ?? []
      const nextContent = content.map((request) => {
        if (request.id !== activeRequest.id) {
          return request
        }
        return {
          ...request,
          departmentId: context.department?.id ?? request.departmentId,
          departmentName: context.department?.name ?? request.departmentName,
          departmentCode: context.department?.code ?? request.departmentCode,
          leaveTypeId: context.leaveType?.id ?? request.leaveTypeId,
          leaveTypeName: context.leaveType?.name ?? request.leaveTypeName,
          leaveTypeCode: context.leaveType?.code ?? request.leaveTypeCode,
          startDate: updatePayload.startDate ?? request.startDate,
          endDate: updatePayload.endDate ?? request.endDate,
          reason: updatePayload.reason ?? request.reason,
          workingDays: updatePayload.requestedDays ?? request.workingDays,
          attachmentIds: updatePayload.attachmentIds ?? request.attachmentIds,
          coveringUserId: updatePayload.coveringUserId,
          updatedAt: new Date().toISOString(),
        }
      })
      return {
        ...previous,
        data: {
          ...previousData,
          content: nextContent,
        },
      }
    })
    toast.success(response.data.message || 'Leave request updated successfully.')
    setEditDialogOpen(false)
  }, [activeRequest, myRequestsQuery.queryKey, queryClient, updateLeaveMutation])

  const handleConfirmRevert = useCallback(async () => {
    if (!activeRequest?.id) {
      setRevertDialogOpen(false)
      return
    }
    try {
      const response = await revertLeaveMutation.mutateAsync({ id: activeRequest.id })
      if (!response?.data?.success) {
        throw new Error(response?.data?.message || 'Unable to cancel leave request.')
      }
      queryClient.setQueryData(myRequestsQuery.queryKey, (previous) => {
        if (!previous) {
          return previous
        }
        const previousData = previous.data
        const content = previousData?.content ?? []
        const nextContent = content.map((request) => {
          if (request.id !== activeRequest.id) {
            return request
          }
          return {
            ...request,
            status: LeaveRequestStatus.CANCELLED,
            updatedAt: new Date().toISOString(),
          }
        })
        return {
          ...previous,
          data: {
            ...previousData,
            content: nextContent,
          },
        }
      })
      toast.success(response.data.message || 'Leave request cancelled successfully.')
      void refetchMyRequests()
    }
    catch (e: unknown) {
      handleError(e as import('axios').AxiosError, 'Failed to cancel leave request. Please try again later.')
    }
    finally {
      setRevertDialogOpen(false)
    }
  }, [
    activeRequest,
    myRequestsQuery.queryKey,
    queryClient,
    refetchMyRequests,
    revertLeaveMutation,
  ])

  const trackingQuery = useTrackLeaveApplication(activeRequest?.id ?? 0, {
    query: {
      enabled: (trackingDialogOpen || detailsDialogOpen) && Boolean(activeRequest?.id),
    },
  })

  const trackingData = trackingQuery.data?.data
  const approvalSteps = trackingData?.approvalSteps ?? []
  const trackingStatus = trackingData?.currentStatus
  const trackingStatusLabel = trackingStatus ? trackingStatus.replaceAll('_', ' ') : '-'
  const trackingStatusVariant = trackingStatus === LeaveRequestStatus.CANCELLED
    || trackingStatus === LeaveRequestStatus.REJECTED
    || trackingStatus === LeaveRequestStatus.CEO_REJECTED
    ? 'destructive'
    : trackingStatus === LeaveRequestStatus.CEO_APPROVED
      ? 'success'
      : trackingStatus?.includes('RECOMMEND') || trackingStatus === LeaveRequestStatus.HR_ACKNOWLEDGEMENT
        ? 'warning'
        : trackingStatus === LeaveRequestStatus.SUBMITTED
          ? 'info'
          : trackingStatus === LeaveRequestStatus.DRAFT
            ? 'secondary'
            : 'outline'

  if (myEligibleLeaveTypesQuery.isError) {
    handleError(myEligibleLeaveTypesQuery.error || 'Failed to load eligible leave types. Please try again later.')
  }

  const editInitialState = useMemo<LeaveRequestFormState | undefined>(() => {
    if (!activeRequest) {
      return undefined
    }
    return ({
      leaveTypeId: activeRequest.leaveType?.id?.toString() ?? '',
      startDate: activeRequest.startDate ?? '',
      endDate: activeRequest.endDate ?? '',
      reason: activeRequest.reason ?? '',
      requestedDays: activeRequest.workingDays?.toString() ?? '',
      coveringUserId: activeRequest.coveringUserId,
    })
  }, [activeRequest])

  const columns = useMemo<ColumnConfig<LeaveRequest>[]>(() => ([
    // {
    //   key: 'id',
    //   header: 'Request ID',
    //   type: 'number',
    // },
    {
      key: 'leaveType',
      header: 'Leave Type',
      cell: (_value, row) => row.leaveType?.name ?? row.leaveType?.code ?? row.leaveType?.id ?? '-',
    },
    // {
    //   key: 'department',
    //   header: 'Department',
    //   cell: (_value, row) => row.department?.name ?? row.department?.code ?? row.department?.id ?? '-',
    // },
    {
      key: 'startDate',
      header: 'Start Date',
      type: 'date',
      cell: (value) => {
        return value ? formatDate(value as string | Date) : '-'
      },
    },
    {
      key: 'endDate',
      header: 'End Date',
      type: 'date',
      cell: (value) => {
        return value ? formatDate(value as string | Date) : '-'
      },
    },
    {
      key: 'workingDays',
      header: 'Days',
      type: 'number',
    },
    {
      key: 'status',
      header: 'Status',
      cell: (_value, row) => {
        const status = row.status
        const label = status ? status.replaceAll('_', ' ') : 'Unknown'
        const variant = status === LeaveRequestStatus.CANCELLED
          || status === LeaveRequestStatus.REJECTED
          || status === LeaveRequestStatus.CEO_REJECTED
          ? 'destructive'
          : status === LeaveRequestStatus.CEO_APPROVED
            ? 'success'
            : status?.includes('RECOMMEND') || status === LeaveRequestStatus.HR_ACKNOWLEDGEMENT
              ? 'warning'
              : status === LeaveRequestStatus.SUBMITTED
                ? 'info'
                : status === LeaveRequestStatus.DRAFT
                  ? 'secondary'
                  : 'outline'
        return <Badge variant={variant}>{label}</Badge>
      },
    },
    {
      key: 'actions',
      header: 'Actions',
      sortable: false,
      filterable: false,
      cell: (_value, row) => {
        const status = row.status
        const canEdit = status === LeaveRequestStatus.DRAFT
          || status === LeaveRequestStatus.SUBMITTED
          || !status
        const canRevert = status !== LeaveRequestStatus.CANCELLED
        return (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" variant="ghost" size="sm" className="h-8 w-8 p-0">
                  <MoreHorizontal className="h-4 w-4" />
                  <span className="sr-only">Open actions</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  disabled={!row.id}
                  onClick={() => handleOpenTracking(row)}
                >
                  Track
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleOpenDetails(row)}>
                  Details
                </DropdownMenuItem>
                <DropdownMenuItem
                  disabled={!row.id || !canEdit}
                  onClick={() => handleOpenEdit(row)}
                >
                  Edit
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  variant="destructive"
                  disabled={!row.id || !canRevert}
                  onClick={() => handleOpenRevert(row)}
                >
                  Cancel
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        )
      },
    },
  ]), [handleOpenDetails, handleOpenEdit, handleOpenRevert, handleOpenTracking])

  return (
    <div className="space-y-6">
      <Dialog open={requestDialogOpen} onOpenChange={handleRequestDialogChange}>
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Request Leave</DialogTitle>
            <DialogDescription>Submit a new leave request for approval.</DialogDescription>
          </DialogHeader>
          <LeaveRequestForm
            key={requestFormKey}
            onSubmitted={handleRequestSubmitted}
            myEligibleLeaveTypesData={myEligibleLeaveTypesQuery.data?.data?.content ?? []}
            leaveTypesLoading={myEligibleLeaveTypesQuery.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={detailsDialogOpen}
        onOpenChange={(open) => {
          setDetailsDialogOpen(open)
          if (!open) {
            setActiveRequest(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle>Leave Details</DialogTitle>
            <DialogDescription>Summary of the selected leave application.</DialogDescription>
          </DialogHeader>
          {activeRequest
            ? (
              <div className="grid gap-4 sm:grid-cols-3">
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Leave Type</p>
                  <p className="text-sm font-medium">
                    {activeRequest.leaveType?.name ?? activeRequest.leaveType?.code ?? activeRequest.leaveType?.id ?? '-'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Start Date</p>
                  <p className="text-sm font-medium">{activeRequest.startDate ? formatDate(activeRequest.startDate) : '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">End Date</p>
                  <p className="text-sm font-medium">{activeRequest.endDate ? formatDate(activeRequest.endDate) : '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Requested Days</p>
                  <p className="text-sm font-medium">{activeRequest.workingDays ?? '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Status</p>
                  <p className="text-sm font-medium">{activeRequest.status ?? '-'}</p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Covering Person</p>
                  <p className="text-sm font-medium">
                    {trackingData?.coveringUserName ?? activeRequest.coveringUserId ?? '-'}
                  </p>
                </div>
                <div className="space-y-1">
                  <p className="text-sm text-muted-foreground">Submitted At</p>
                  <p className="text-sm font-medium">{activeRequest.createdAt ? formatDateTime(activeRequest.createdAt) : '-'}</p>
                </div>
                <div className="space-y-1 sm:col-span-3">
                  <p className="text-sm text-muted-foreground">Reason</p>
                  <p className="text-sm font-medium">{activeRequest.reason ?? '-'}</p>
                </div>
              </div>
            )
            : (
              <p className="text-sm text-muted-foreground">No leave request selected.</p>
            )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={trackingDialogOpen}
        onOpenChange={(open) => {
          setTrackingDialogOpen(open)
          if (!open) {
            setActiveRequest(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-5xl max-h-[90vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Leave Tracking</DialogTitle>
            <DialogDescription>Follow the approval lifecycle for this request.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 overflow-y-auto flex-1">
            {trackingQuery.isPending
              ? (
                <p className="text-sm text-muted-foreground">Loading tracking details...</p>
              )
              : null}
            {trackingQuery.isError
              ? (
                <Alert variant="destructive">
                  <AlertTitle>Unable to load tracking details</AlertTitle>
                  <AlertDescription>{trackingQuery.error?.message ?? 'Please try again.'}</AlertDescription>
                </Alert>
              )
              : null}
            {trackingData
              ? (
                <div className="space-y-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-medium">
                        {typeof trackingData.progressPercentage === 'number'
                          ? `${trackingData.progressPercentage}%`
                          : 'N/A'}
                      </span>
                    </div>
                    <Progress value={trackingData.progressPercentage ?? 0} />
                  </div>

                  <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-3">
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Applicant</p>
                      <p className="text-sm font-medium">{trackingData.applicantName ?? '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Department</p>
                      <p className="text-sm font-medium">{trackingData.department ?? '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Leave Type</p>
                      <p className="text-sm font-medium">{trackingData.leaveType ?? '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Start Date</p>
                      <p className="text-sm font-medium">{trackingData.startDate ? formatDate(trackingData.startDate) : '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">End Date</p>
                      <p className="text-sm font-medium">{trackingData.endDate ? formatDate(trackingData.endDate) : '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Requested Days</p>
                      <p className="text-sm font-medium">
                        {typeof trackingData.workingDays === 'number' ? trackingData.workingDays : '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Current Status</p>
                      <Badge variant={trackingStatusVariant} className="mt-1">
                        {trackingStatusLabel}
                      </Badge>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Submitted At</p>
                      <p className="text-sm font-medium">{trackingData.submittedAt ? formatDateTime(trackingData.submittedAt) : '-'}</p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Approval Levels</p>
                      <p className="text-sm font-medium">
                        {typeof trackingData.currentApprovalLevel === 'number'
                          ? trackingData.currentApprovalLevel
                          : '-'}
                        {' / '}
                        {typeof trackingData.totalApprovalLevels === 'number'
                          ? trackingData.totalApprovalLevels
                          : '-'}
                      </p>
                    </div>
                    <div className="space-y-1">
                      <p className="text-sm text-muted-foreground">Covering User</p>
                      <p className="text-sm font-medium">
                        {trackingData.coveringUserName ?? trackingData.coveringUserId ?? '-'}
                      </p>
                    </div>
                  </div>

                  <div className="mt-8">
                    <h3 className="text-lg font-semibold mb-6">Approval Timeline</h3>
                    {trackingQuery.isPending
                      ? (
                        <p className="text-sm text-muted-foreground">Loading timeline...</p>
                      )
                      : (
                        <VerticalEventTimeline
                          events={approvalSteps.map((step, index): TimelineEvent => {
                            // Find the first incomplete step (by stepNumber)
                            const firstIncompleteStep = approvalSteps.find(s => !s.isCompleted && !s.isSkipped)
                            const isCurrentStep = firstIncompleteStep?.stepNumber === step.stepNumber
                            return {
                              id: step.stepNumber ?? index,
                              title: step.roleTitle ?? `Step ${step.stepNumber ?? index + 1}`,
                              role: step.roleTitle,
                              approver: step.expectedApproverName,
                              action: step.action,
                              timestamp: step.actionTimestamp ? formatDateTime(step.actionTimestamp) : undefined,
                              isCompleted: step.isCompleted ?? false,
                              isSkipped: step.isSkipped ?? false,
                              isCurrent: isCurrentStep,
                              description: step.comments
                                ? step.comments
                                : step.roleTitle
                                  ? `${step.roleTitle} - ${step.expectedApproverName ?? 'No approver assigned'}`
                                  : undefined,
                            }
                          })}
                        />
                      )}
                  </div>
                </div>
              )
              : (
                <p className="text-sm text-muted-foreground">
                  {activeRequest?.id
                    ? 'No tracking details available for this request.'
                    : 'Select a leave request to view tracking.'}
                </p>
              )}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editDialogOpen}
        onOpenChange={(open) => {
          setEditDialogOpen(open)
          if (!open) {
            setActiveRequest(null)
          }
        }}
      >
        <DialogContent className="sm:max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Leave Request</DialogTitle>
            <DialogDescription>Update the request before it is recommended or approved.</DialogDescription>
          </DialogHeader>
          <LeaveRequestForm
            key={activeRequest?.id ?? 'edit-leave-form'}
            initialState={editInitialState}
            initialAttachmentIds={activeRequest?.attachmentIds ?? []}
            departmentIdOverride={activeRequest?.department?.id}
            myEligibleLeaveTypesData={myEligibleLeaveTypesQuery.data?.data?.content ?? []}
            leaveTypesLoading={myEligibleLeaveTypesQuery.isPending}
            submitLabel="Save Changes"
            resetLabel="Reset Changes"
            onSubmitted={handleRequestSubmitted}
            onSubmitOverride={handleEditSubmit}
          />
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={revertDialogOpen}
        onOpenChange={(open) => {
          setRevertDialogOpen(open)
          if (!open) {
            setActiveRequest(null)
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Cancel leave request?</AlertDialogTitle>
            <AlertDialogDescription>
              This marks the leave request as cancelled. You can submit a new request if needed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep Request</AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirmRevert}>
              Cancel Request
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {myRequestsQuery.isError
        ? (
          <Alert variant="destructive">
            <AlertTitle>Unable to load leave applications</AlertTitle>
            <AlertDescription>{myRequestsQuery.error?.message ?? 'Please refresh the page.'}</AlertDescription>
          </Alert>
        )
        : null}
      <EnhancedDataTable
        data={sortedRequests}
        columns={columns}
        emptyStateMessage="No leave applications submitted yet."
        loading={myRequestsQuery.isPending}
        enableColumnFilters={false}
        enableExport={false}
      />
    </div>
  )
}
